import { useRef, useState } from 'preact/hooks';
import type { RefObject } from 'preact';
import { useLocation } from 'wouter-preact';
import { AlertCircle, Bell, CheckCheck } from 'lucide-preact';
import { Popover } from '../overlays/Popover';
import { Tabs, TabPanel } from '../overlays/Tabs';
import { Avatar } from '../display/Avatar';
import { EmptyState } from '../feedback/EmptyState';
import {
  hasUrgentUnread,
  markAllRead,
  markRead,
  notifications,
  unreadCount,
  type NotificationItem,
  type NotificationSeverity,
} from '@/stores/notifications';
import { useNotifications } from '@/hooks/useNotifications';
import { formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';

export function NotificationCenter() {
  // Starts polling via useQuery + refetchInterval; syncs into store signals.
  useNotifications();

  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'all' | 'unread'>('all');
  const [, navigate] = useLocation();
  const list = notifications.value;
  const unread = unreadCount.value;
  const urgent = hasUrgentUnread.value;
  const visible = tab === 'unread' ? list.filter((n) => !n.read) : list;

  function handleClick(notif: NotificationItem) {
    void markRead(notif.id);
    setOpen(false);
    if (notif.href) {
      // Internal links go through wouter; external (https://) abren en nueva pestaña.
      if (notif.href.startsWith('http://') || notif.href.startsWith('https://')) {
        window.open(notif.href, '_blank', 'noopener,noreferrer');
      } else {
        navigate(notif.href);
      }
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Notificaciones${unread > 0 ? ` (${unread} sin leer)` : ''}`}
        className="text-fg-muted hover:bg-surface-2 hover:text-fg-strong relative flex h-9 w-9 items-center justify-center rounded-md transition-colors"
      >
        <Bell size={16} />
        {unread > 0 ? (
          <span
            className={cn(
              'bg-error text-error-contrast text-3xs ring-surface absolute top-1 right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 font-semibold ring-2',
              urgent && 'animate-pulse',
            )}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchor={triggerRef as RefObject<HTMLElement>}
        placement="bottom-end"
        className="w-[360px] overflow-hidden p-0"
      >
        <header className="border-border-subtle flex items-center justify-between border-b px-3 py-2">
          <p className="text-13 text-fg-strong font-semibold">Notificaciones</p>
          <button
            type="button"
            onClick={() => void markAllRead()}
            disabled={unread === 0}
            className="text-fg-muted hover:bg-surface-2 hover:text-fg-strong inline-flex items-center gap-1 rounded px-2 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-40"
          >
            <CheckCheck size={12} />
            Marcar todas como leídas
          </button>
        </header>
        <div className="border-border-subtle border-b px-3">
          <Tabs
            size="sm"
            value={tab}
            onChange={setTab}
            items={[
              { value: 'all', label: 'Todas', count: list.length },
              { value: 'unread', label: 'Sin leer', count: unread },
            ]}
          />
        </div>
        <div className="max-h-80 overflow-auto">
          <TabPanel value={tab} active={tab}>
            {visible.length === 0 ? (
              <EmptyState
                size="sm"
                icon={Bell}
                title="Sin notificaciones"
                description="Te avisaremos cuando pase algo importante."
              />
            ) : (
              <ul>
                {visible.map((n) => (
                  <NotificationRow key={n.id} notif={n} onClick={() => handleClick(n)} />
                ))}
              </ul>
            )}
          </TabPanel>
        </div>
      </Popover>
    </>
  );
}

// Acento lateral por severidad. info no lleva acento (borde transparente para
// mantener la alineación con las filas que sí lo tienen).
const SEVERITY_ACCENT: Record<NotificationSeverity, string> = {
  info: 'border-l-transparent',
  warning: 'border-l-warning',
  urgent: 'border-l-error',
};

function NotificationRow({ notif, onClick }: { notif: NotificationItem; onClick: () => void }) {
  const severity = notif.severity ?? 'info';
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'border-border-subtle hover:bg-surface-2 flex w-full items-start gap-2.5 border-b border-l-2 px-3 py-2.5 text-left transition-colors',
          SEVERITY_ACCENT[severity],
          !notif.read && 'bg-brand-tint/30',
        )}
      >
        {severity === 'urgent' ? (
          <span className="bg-error-bg text-error flex h-7 w-7 shrink-0 items-center justify-center rounded-full">
            <AlertCircle size={16} />
          </span>
        ) : (
          <Avatar name={notif.actor ?? 'sistema'} size="sm" />
        )}
        <div className="flex-1">
          <p className="text-13 text-fg-strong font-medium">{notif.title}</p>
          {notif.body ? <p className="text-fg-muted mt-0.5 text-xs">{notif.body}</p> : null}
          <p className="text-2xs text-fg-faint mt-1">{formatRelative(notif.at)}</p>
        </div>
        {!notif.read ? (
          <span className="bg-brand mt-1 h-2 w-2 rounded-full" aria-label="No leída" />
        ) : null}
      </button>
    </li>
  );
}
